import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, Key, X } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';
import { toast } from 'react-toastify';

const SecuritySettingsPage = ({ lang }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(lang);

  const [formData, setFormData] = useState({
    password: '',
    new_password: '',
    password_confirmation: ''
  });
  
  const [showPassword, setShowPassword] = useState({
    password: false,
    new_password: false,
    password_confirmation: false
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  
  // États pour la modale de validation
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [token, setToken] = useState("");
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [isValidationSuccess, setIsValidationSuccess] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Effacer l'erreur du champ quand l'utilisateur commence à taper
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = t('currentPasswordRequired');
    }
    
    if (!formData.new_password) {
      newErrors.new_password = t('newPasswordRequired');
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = t('passwordMinLength');
    }
    
    if (!formData.password_confirmation) {
      newErrors.password_confirmation = t('confirmPasswordRequired');
    } else if (formData.new_password !== formData.password_confirmation) {
      newErrors.password_confirmation = t('passwordsDoNotMatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    try {
            const baseRequestBody = {
        password: formData.password,
        new_password: formData.new_password,
        passwod_confirmation: formData.password_confirmation
      };
      const response = await api.post('/profile/update/password', baseRequestBody);

      if (response.data) {
        // Afficher la modale pour saisir le token
        setShowTokenModal(true);
        setValidationMessage(t('tokenEmailSentPassword'));
        setIsValidationSuccess(false);
        setToken("");
      }
    } catch (error) {
      console.error(t('errorUpdatingPassword'), error);
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: t('passwordUpdateError') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenValidation = async () => {
    if (!token.trim()) {
      setValidationMessage(t('enterValidationCode'));
      return;
    }

    setIsValidatingToken(true);
    setValidationMessage("");

    try {
      const response = await api.get(`/profile/update/password/${token}/validate`);


      if (response.status === 200 || response.status === 201 || response.status === 202) {
        setIsValidationSuccess(true);
        setValidationMessage(t('passwordUpdatedSuccess'));        

        // Réinitialiser le formulaire
        setFormData({
          password: '',
          new_password: '',
          password_confirmation: ''
        });
        setErrors({});

                toast.success(t('profileUpdatedSuccess'));
        setTimeout(() => {
          setShowTokenModal(false);
          setToken("");
          setValidationMessage("");
          setIsValidationSuccess(false);
          navigate('/settings');
        }, 2000);
      } else {
        setIsValidationSuccess(false);
        setValidationMessage(response.data.result?.message || t('invalidValidationCode'));
      }
    } catch (error) {
      console.error(t('errorValidatingToken'), error);
      setIsValidationSuccess(false);
      setValidationMessage(t('validationErrorTryAgain'));
    } finally {
      setIsValidatingToken(false);
    }
  };

  const handleCancelUpdate = () => {
    setShowTokenModal(false);
    setToken("");
    setValidationMessage("");
    setIsValidationSuccess(false);
  };

  const getInputStyle = (field) => {
    const baseStyle = styles.input;
    const isFocused = focusedField === field;
    const hasError = errors[field];
    
    let style = { ...baseStyle };
    
    if (isFocused) {
      style = { ...style, ...styles.inputFocus };
    }
    
    if (hasError) {
      style = { ...style, ...styles.inputError };
    }
    
    return style;
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh',
      position: 'relative'
    },
    header: {
      width: '100%',
      maxWidth: '500px',
      marginBottom: '32px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      cursor: 'pointer',
      color: '#374151',
      marginBottom: '16px',
      fontFamily: 'inherit'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    form_container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '32px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '500px'
    },
    formGroup: {
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px'
    },
    inputContainer: {
      position: 'relative',
      width: '100%'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      paddingRight: '50px',
        borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '#d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
      fontFamily: 'inherit'
    },
    inputFocus: {
      borderColor: '#7c3aed',
      boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
      outline: 'none'
    },
    inputError: {
      borderColor: '#ef4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)'
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '4px',
      fontFamily: 'inherit'
    },
    errorMessage: {
      color: '#ef4444',
      fontSize: '12px',
      marginTop: '4px'
    },
    submitButton: {
      width: '100%',
      padding: '14px 24px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      marginTop: '8px',
      fontFamily: 'inherit'
    },
    submitButtonDisabled: {
      background: '#9ca3af',
      cursor: 'not-allowed'
    },
    requirements: {
      marginTop: '24px',
      padding: '20px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    requirementTitle: {
      fontWeight: '600',
      marginBottom: '12px',
      color: '#374151',
      fontSize: '16px'
    },
    requirementList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    requirementItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
      fontSize: '14px',
      color: '#6b7280'
    },
    // Styles pour la modale
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: 'white',
      borderRadius: '12px',
      padding: '32px',
      width: '90%',
      maxWidth: '500px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '8px',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    },
    tokenInput: {
      width: '100%',
      padding: '14px 16px',
        borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: '#d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      fontFamily: 'monospace',
      letterSpacing: '2px',
      textAlign: 'center',
      marginBottom: '16px',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box'
    },
    tokenInputFocus: {
      borderColor: '#7c3aed',
      boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
      outline: 'none'
    },
    validationMessage: {
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '14px',
      lineHeight: '1.5'
    },
    successMessage: {
      background: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0'
    },
    errorMessageModal: {
      background: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fecaca'
    },
    infoMessage: {
      background: '#dbeafe',
      color: '#1e40af',
      border: '1px solid #bfdbfe'
    },
    modalButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '20px'
    },
    tokenButton: {
      padding: '12px 24px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    },
    tokenButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
            secondaryButton: {
            padding: '12px 24px',
            background: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
        }
  };

  return (
    <>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <button 
            style={styles.backButton}
            onClick={() => navigate('/settings')}
            onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.background = 'white'}
          >
            <ArrowLeft size={20} />
            {t('backToSettings')}
          </button>
          
          <div style={styles.titleContainer}>
            <Lock size={32} color="#7c3aed" />
            <h1 style={styles.title}>{t('securitySettings')}</h1>
          </div>
        </div>

        {/* Formulaire de changement de mot de passe */}
        <div style={styles.form_container}>
          <div style={styles.section}>
            <h2 style={{ 
              marginTop: 0,
              marginBottom: '24px', 
              color: '#1f2937',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {t('changePassword')}
            </h2>
            
            {errors.general && (
              <div style={{
                padding: '12px',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {errors.general}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              {/* Mot de passe actuel */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('currentPassword')}</label>
                <div style={styles.inputContainer}>
                  <input
                    type={showPassword.password ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    style={getInputStyle('password')}
                    placeholder={t('enterCurrentPassword')}
                    required
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                  />
                  <button
                    type="button"
                    style={styles.passwordToggle}
                    onClick={() => togglePasswordVisibility('password')}
                    onMouseEnter={(e) => e.target.style.color = '#374151'}
                    onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                  >
                    {showPassword.password ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <div style={styles.errorMessage}>{errors.password}</div>}
              </div>

              {/* Nouveau mot de passe */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('newPassword')}</label>
                <div style={styles.inputContainer}>
                  <input
                    type={showPassword.new_password ? 'text' : 'password'}
                    value={formData.new_password}
                    onChange={(e) => handleChange('new_password', e.target.value)}
                    style={getInputStyle('new_password')}
                    placeholder={t('enterNewPassword')}
                    required
                    onFocus={() => setFocusedField('new_password')}
                    onBlur={() => setFocusedField(null)}
                  />
                  <button
                    type="button"
                    style={styles.passwordToggle}
                    onClick={() => togglePasswordVisibility('new_password')}
                    onMouseEnter={(e) => e.target.style.color = '#374151'}
                    onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                  >
                    {showPassword.new_password ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.new_password && <div style={styles.errorMessage}>{errors.new_password}</div>}
              </div>

              {/* Confirmation du mot de passe */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('confirmPassword')}</label>
                <div style={styles.inputContainer}>
                  <input
                    type={showPassword.password_confirmation ? 'text' : 'password'}
                    value={formData.password_confirmation}
                    onChange={(e) => handleChange('password_confirmation', e.target.value)}
                    style={getInputStyle('password_confirmation')}
                    placeholder={t('confirmNewPassword')}
                    required
                    onFocus={() => setFocusedField('password_confirmation')}
                    onBlur={() => setFocusedField(null)}
                  />
                  <button
                    type="button"
                    style={styles.passwordToggle}
                    onClick={() => togglePasswordVisibility('password_confirmation')}
                    onMouseEnter={(e) => e.target.style.color = '#374151'}
                    onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                  >
                    {showPassword.password_confirmation ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password_confirmation && <div style={styles.errorMessage}>{errors.password_confirmation}</div>}
              </div>

              <button 
                type="submit" 
                style={{
                  ...styles.submitButton,
                  ...(isLoading ? styles.submitButtonDisabled : {})
                }}
                disabled={isLoading}
                onMouseEnter={(e) => !isLoading && (e.target.style.background = '#6d28d9')}
                onMouseLeave={(e) => !isLoading && (e.target.style.background = '#7c3aed')}
              >
                {isLoading ? t('updating') : t('updatePassword')}
              </button>
            </form>

            {/* Exigences de mot de passe */}
            <div style={styles.requirements}>
              <div style={styles.requirementTitle}>{t('passwordRequirements')}</div>
              <ul style={styles.requirementList}>
                <li style={styles.requirementItem}>
                  <CheckCircle size={16} color="#10b981" />
                  {t('minCharacters')}
                </li>
                <li style={styles.requirementItem}>
                  <CheckCircle size={16} color="#10b981" />
                  {t('uppercaseLowercase')}
                </li>
                <li style={styles.requirementItem}>
                  <CheckCircle size={16} color="#10b981" />
                  {t('includeNumbers')}
                </li>
                <li style={styles.requirementItem}>
                  <CheckCircle size={16} color="#10b981" />
                  {t('includeSpecialChars')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de validation par token */}
      {showTokenModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                <Key size={24} color="#7c3aed" />
                {t('validationRequired')}
              </h2>
              <button
                style={styles.closeButton}
                onClick={handleCancelUpdate}
                onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <X size={20} />
              </button>
            </div>

            {validationMessage && (
              <div style={{
                ...styles.validationMessage,
                ...(isValidationSuccess ? styles.successMessage :
                  validationMessage.includes(t('error')) ? styles.errorMessageModal : styles.infoMessage)
              }}>
                {validationMessage}
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '12px', color: '#374151' }}>
                {t('enterValidationCodeEmail')}
              </p>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={t('tokenPlaceholder')}
                style={{
                  ...styles.tokenInput,
                  ...(token ? styles.tokenInputFocus : {})
                }}
                disabled={isValidatingToken || isValidationSuccess}
                onFocus={() => setFocusedField('token')}
                onBlur={() => setFocusedField(null)}
                autoFocus
              />
            </div>

            <div style={styles.modalButtons}>
              <button
                style={styles.secondaryButton}
                onClick={handleCancelUpdate}
                disabled={isValidatingToken}
              >
                {t('cancel')}
              </button>
              <button
                style={{
                  ...styles.tokenButton,
                  ...((isValidatingToken || isValidationSuccess) && styles.tokenButtonDisabled)
                }}
                onClick={handleTokenValidation}
                disabled={isValidatingToken || isValidationSuccess}
                onMouseEnter={(e) => !isValidatingToken && !isValidationSuccess && (e.target.style.background = '#6d28d9')}
                onMouseLeave={(e) => !isValidatingToken && !isValidationSuccess && (e.target.style.background = '#7c3aed')}
              >
                {isValidatingToken ? (
                  <>{t('validating')}</>
                ) : isValidationSuccess ? (
                  <>
                    <CheckCircle size={16} />
                    {t('validated')}
                  </>
                ) : (
                  <>
                    <Key size={16} />
                    {t('validateCode')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SecuritySettingsPage;